import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useConfirm } from '../../context/ConfirmContext';
import {
  useEmailTemplates,
  useDeleteEmailTemplate,
  useUpdateEmailTemplate,
} from '../../api/email';
import type { EmailTemplate } from '../../types/email';

export function EmailTemplatesPage() {
  const confirm = useConfirm();
  const [showInactive, setShowInactive] = useState(false);
  const { data, isLoading, error } = useEmailTemplates(!showInactive);
  const deleteMutation = useDeleteEmailTemplate();
  const updateMutation = useUpdateEmailTemplate();

  const handleDelete = async (template: EmailTemplate) => {
    const confirmed = await confirm({
      title: 'Vorlage löschen',
      message: `Möchten Sie die Vorlage "${template.name}" wirklich löschen?`,
      confirmLabel: 'Löschen',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await deleteMutation.mutateAsync(template.id);
        toast.success('Vorlage erfolgreich gelöscht');
      } catch (error) {
        console.error('Failed to delete template:', error);
        toast.error('Fehler beim Löschen der Vorlage');
      }
    }
  };

  const handleToggleActive = async (template: EmailTemplate) => {
    try {
      await updateMutation.mutateAsync({
        id: template.id,
        data: { isActive: !template.isActive },
      });
      toast.success(template.isActive ? 'Vorlage deaktiviert' : 'Vorlage aktiviert');
    } catch (error) {
      console.error('Failed to toggle template:', error);
      toast.error('Fehler beim Aendern des Status');
    }
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Fehler beim Laden der Vorlagen</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">E-Mail Vorlagen</h1>
          <p className="mt-1 text-sm text-gray-500">
            Verwalten Sie E-Mail Vorlagen fuer automatische Benachrichtigungen
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <label className="flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Inaktive anzeigen
          </label>
          <Link
            to="/email/templates/new"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Neue Vorlage
          </Link>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Laden...</p>
          </div>
        ) : data?.data.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Vorlagen gefunden</h3>
            <p className="mt-1 text-sm text-gray-500">
              Erstellen Sie eine neue E-Mail Vorlage.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {data?.data.map((template) => (
              <div
                key={template.id}
                className={`p-6 hover:bg-gray-50 ${!template.isActive ? 'bg-gray-50 opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {template.name}
                      </h3>
                      {template.isSystem && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          System
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          template.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {template.isActive ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                    {template.description && (
                      <p className="mt-1 text-sm text-gray-500">{template.description}</p>
                    )}
                    <div className="mt-2 text-sm text-gray-600">
                      <strong>Betreff:</strong> {template.subject}
                    </div>
                    <div className="mt-2 flex items-center text-xs text-gray-400 space-x-4">
                      <span>
                        Erstellt: {new Date(template.createdAt).toLocaleDateString('de-DE')}
                      </span>
                      {template.createdBy && (
                        <span>
                          von {template.createdBy.firstName} {template.createdBy.lastName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                    <button
                      onClick={() => handleToggleActive(template)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        template.isActive
                          ? 'text-yellow-700 hover:bg-yellow-100'
                          : 'text-green-700 hover:bg-green-100'
                      }`}
                      disabled={updateMutation.isPending}
                    >
                      {template.isActive ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                    <Link
                      to={`/email/templates/${template.id}`}
                      className="px-3 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded-md"
                    >
                      Bearbeiten
                    </Link>
                    {!template.isSystem && (
                      <button
                        onClick={() => handleDelete(template)}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md"
                        disabled={deleteMutation.isPending}
                      >
                        Loeschen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
